<template>
	<Transition appear name="fade-up-delayed-long">
		<div class="bt-hint-container" v-bind="$attrs">
			<div v-if="forced" class="bt-hint-backdrop"/>
			
			<div
					ref="hintRef"
					class="bt-hint-popup"
					:class="[positionClass, `arrow-${arrowDirection}`]"
					:style="computedStyle"
			>
				<Icon
						:name="`arrow-${arrowDirection}`"
						:class="['bt-popup-arrow', `bounce-${arrowDirection}`]"
						size="lg"
				/>
				
				<div class="bt-popup-content">
					<slot/>
				</div>
			</div>
		</div>
	</Transition>
</template>

<script setup>
import {computed, ref, onMounted, onBeforeUnmount, watch} from 'vue'
import Icon from './Icon.vue'

defineOptions({
	inheritAttrs: false
})

const props = defineProps({
	position: {
		type: String,
		default: 'absolute',
		validator: (val) => ['absolute', 'fixed', 'relative'].includes(val)
	},
	arrowDirection: {
		type: String,
		default: 'up',
		validator: (val) => ['up', 'down', 'left', 'right'].includes(val)
	},
	offsets: {
		type: Object,
		default: () => ({})
	},
	targetSelector: {
		type: String,
		default: ''
	},
	forced: {
		type: Boolean,
		default: false
	},
	centerX: {
		type: Boolean,
		default: false
	},
	centerY: {
		type: Boolean,
		default: false
	}
})

const hintRef = ref(null)
const positionClass = computed(() => `position-${props.position}`)
const computedStyle = computed(() => {
	let transform = ''
	if (props.centerX && props.centerY) transform = 'translate(-50%, -50%)'
	else if (props.centerX) transform = 'translateX(-50%)'
	else if (props.centerY) transform = 'translateY(-50%)'
	
	return {
		top: props.centerY ? '50%' : (props.offsets.top || undefined),
		bottom: props.centerY ? undefined : (props.offsets.bottom || undefined),
		left: props.centerX ? '50%' : (props.offsets.left || undefined),
		right: props.centerX ? undefined : (props.offsets.right || undefined),
		transform: transform || undefined
	}
})
let observer = null

const toggleHighlight = (add) => {
	if (!props.targetSelector) return
	const el = document.querySelector(props.targetSelector)
	if (el) {
		if (add) {
			el.classList.add('bt-target-highlight')
		} else {
			el.classList.remove('bt-target-highlight')
		}
	}
}

const initSetup = () => {
	if (observer) {
		observer.disconnect()
		observer = null
	}
	
	if (!props.targetSelector) return
	
	toggleHighlight(false)
	
	const targetEl = document.querySelector(props.targetSelector)
	if (targetEl) {
		toggleHighlight(true)
		return
	}
	
	observer = new MutationObserver(() => {
		const el = document.querySelector(props.targetSelector)
		if (el) {
			toggleHighlight(true)
			observer.disconnect()
			observer = null
		}
	})
	observer.observe(document.body, {childList: true, subtree: true})
}

onMounted(() => {
	initSetup()
})

onBeforeUnmount(() => {
	toggleHighlight(false)
	if (observer) observer.disconnect()
})

watch(() => props.targetSelector, () => {
	initSetup()
}, {flush: 'post'})
</script>

<style>
.bt-hint-popup {
	z-index: 10;
	display: flex;
	align-items: center;
	pointer-events: none;
}

.bt-hint-popup.position-absolute {
	position: absolute;
}

.bt-hint-popup.position-fixed {
	position: fixed;
}

.bt-hint-popup.position-relative {
	position: relative;
}

.bt-hint-popup.arrow-up {
	flex-direction: column;
}

.bt-hint-popup.arrow-down {
	flex-direction: column-reverse;
}

.bt-hint-popup.arrow-left {
	flex-direction: row;
}

.bt-hint-popup.arrow-right {
	flex-direction: row-reverse;
}

.bt-popup-arrow {
	filter: drop-shadow(var(--shadow-md));
}

.bt-popup-content {
	background: var(--color-popup-dark-2);
	border: var(--border-thin) var(--color-primary);
	border-radius: var(--border-radius-lg);
	padding: var(--spacing-sm) var(--spacing-lg);
	white-space: nowrap;
	backdrop-filter: blur(var(--blur-sm));
	-webkit-backdrop-filter: blur(var(--blur-sm));
	pointer-events: auto;
	
	transition: box-shadow 0.3s cubic-bezier(0.16, 1, 0.3, 1) !important;
	animation: bt-pulse-highlight 2s infinite alternate !important;
	box-shadow: var(--shadow-md), 0 0 16px 0 var(--color-primary);
}

.bt-target-highlight {
	position: relative !important;
	pointer-events: auto !important;
	outline: var(--border-thin) var(--color-primary) !important;
	box-shadow: var(--shadow-md) !important;
	transition: box-shadow 0.3s cubic-bezier(0.16, 1, 0.3, 1) !important;
	animation: bt-pulse-highlight 2s infinite alternate !important;
}

.bt-popup-content span {
	font-weight: var(--font-weight-semibold, 600);
}

@keyframes bt-pulse-highlight {
	0% {
		box-shadow: 0 0 0 2px var(--color-primary), 0 0 0 0 var(--color-primary) !important;
	}
	100% {
		box-shadow: 0 0 0 4px var(--color-primary), 0 0 16px 6px var(--color-primary) !important;
	}
}

.bounce-up {
	animation: bt-bounce-up 1.2s ease-in-out infinite;
}

.bounce-down {
	animation: bt-bounce-down 1.2s ease-in-out infinite;
}

.bounce-left {
	animation: bt-bounce-left 1.2s ease-in-out infinite;
}

.bounce-right {
	animation: bt-bounce-right 1.2s ease-in-out infinite;
}

@keyframes bt-bounce-up {
	0%, 100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-6px);
	}
}

@keyframes bt-bounce-down {
	0%, 100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(6px);
	}
}

@keyframes bt-bounce-left {
	0%, 100% {
		transform: translateX(0);
	}
	50% {
		transform: translateX(-6px);
	}
}

@keyframes bt-bounce-right {
	0%, 100% {
		transform: translateX(0);
	}
	50% {
		transform: translateX(6px);
	}
}
</style>